/**
 * 统一的 fetch 封装:
 * - 自动注入 Bearer token
 * - 401 先用 refresh token 换一次 access token 并重试; 仍失败才清凭据跳登录
 *   (403 不会 —— 见下方说明)
 * - AbortController 超时 (默认 30s, 长耗时接口可单独传 timeoutMs)
 * - 304 直接返回 undefined (配合 TanStack Query 的 staleTime 用)
 */
import { useAuthStore } from "@/stores/auth-store";

const BASE = import.meta.env.VITE_API_BASE || "";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API ${status}`);
    this.status = status;
    this.body = body;
  }
}

export interface ApiFetchOptions extends RequestInit {
  /** 默认 30000ms; render-image 这种长耗时接口可传 120000 */
  timeoutMs?: number;
  /** 跳过 Authorization 注入 (登录接口本身); 这类接口的 401 原样抛给调用方 */
  skipAuth?: boolean;
}

/** 登录页的完整路径。basename 是 /admin, 必须带上 BASE_URL, 不能写死 "/login" */
const LOGIN_PATH = `${import.meta.env.BASE_URL}login`;

/** 并发的多个 401 共用一次 refresh 请求 */
let refreshing: Promise<boolean> | null = null;

/** 用 refresh token 换新 access token; 成功返回 true。直接用 fetch, 不经 apiFetch, 避免递归 */
function tryRefreshToken(): Promise<boolean> {
  const rt = useAuthStore.getState().refreshToken;
  if (!rt) return Promise.resolve(false);
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(`${BASE}/api/refresh-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: rt }),
        });
        if (!res.ok) return false;
        const data = (await res.json().catch(() => null)) as { access_token?: string } | null;
        if (!data?.access_token) return false;
        useAuthStore.getState().setAccessToken(data.access_token);
        return true;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

function redirectToLogin() {
  useAuthStore.getState().clear();
  // 用 location 而非 navigate, 因为这里在 React 树外
  if (typeof window !== "undefined" && !window.location.pathname.startsWith(LOGIN_PATH)) {
    window.location.href = LOGIN_PATH;
  }
}

export async function apiFetch<T>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  return doFetch<T>(path, opts, false);
}

/** 同 apiFetch (带 token / 401 刷新 / 超时), 但把响应体当二进制返回 (文件下载用) */
export async function apiFetchBlob(path: string, opts: ApiFetchOptions = {}): Promise<Blob> {
  return doFetch<Blob>(path, opts, false, "blob");
}

async function doFetch<T>(
  path: string,
  opts: ApiFetchOptions,
  retried: boolean,
  as: "json" | "blob" = "json",
): Promise<T> {
  const { timeoutMs = 30000, skipAuth, ...rest } = opts;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const token = useAuthStore.getState().token;
  const headers = new Headers(rest.headers);
  if (token && !skipAuth) headers.set("Authorization", `Bearer ${token}`);
  if (rest.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");

  try {
    const res = await fetch(`${BASE}${path}`, { ...rest, headers, signal: controller.signal });

    // 401 与 403 语义不同, 必须区别对待:
    //   401 = 没登录 / token 失效  → 先试 refresh, 不行再清凭据跳登录
    //   403 = 已登录, 只是这个资源没权限 → 绝不能清凭据
    // 曾把两者混为一谈: company_admin 登录后, admin 页请求了 super_admin
    // 专属的 /api/admin/companies 拿到 403, 于是刚登录就被自己踢回登录页,
    // 表现为 "密码明明对却登不进去"。403 交给调用方自己处理。
    if (res.status === 401) {
      const errBody = await res.json().catch(() => null);
      // 登录 / 选公司 / 短信登录等公开接口: 401 是业务错误 (密码错、票据过期), 交给页面展示
      if (skipAuth) throw new ApiError(401, errBody, "未登录或登录已过期");
      if (!retried && (await tryRefreshToken())) {
        clearTimeout(timer);
        return doFetch<T>(path, opts, true, as);
      }
      redirectToLogin();
      throw new ApiError(res.status, errBody, "未登录或登录已过期");
    }

    if (res.status === 403) {
      const errBody = await res.json().catch(() => null);
      throw new ApiError(403, errBody, "无权限访问该资源");
    }

    if (res.status === 304) return undefined as T;

    if (!res.ok) {
      const errBody = await res.json().catch(() => null);
      throw new ApiError(res.status, errBody);
    }

    // 兼容空响应 (204)
    if (res.status === 204) return undefined as T;

    if (as === "blob") return (await res.blob()) as T;
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

/** 取 FastAPI 错误的 detail 文本 */
export function errorDetail(err: unknown, fallback = "操作失败"): string {
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: unknown } | null)?.detail;
    if (typeof detail === "string" && detail) return detail;
    if (err.status === 402) return "余额不足，请充值";
    return `${fallback} (${err.status})`;
  }
  if (err instanceof DOMException && err.name === "AbortError") return "请求超时，请稍后重试";
  return "网络错误，请稍后重试";
}
